import React, { ComponentProps, useCallback, useMemo } from 'react'
import { isRoomsInvalid } from 'lib/search/searchUtils'
import TextButton from 'LuxKit/Button/TextButton'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getDurationPackageOptions, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import { useMultiRoomBookingGuard } from 'businessTraveller/hooks/useMultiRoomBookingGuard'

interface Props extends Omit<ComponentProps<typeof TextButton>, 'kind' | 'children'> {
  rooms: Array<App.RoomOccupants>;
  checkIn?: string;
  roomType?: App.PackageRoomType;
  packageOptions: Array<OfferPackageOptionView>;
  isFlightSelected?: boolean;
  activeFilter?: OfferPageFilterType;
  onSubmit?: () => void;
  durationSelected?: boolean;
}

function AccommodationMobileViewOptionsButton(props: Props) {
  const {
    rooms,
    checkIn,
    roomType,
    packageOptions,
    isFlightSelected,
    activeFilter,
    onSubmit,
    onClick,
    durationSelected,
    ...rest
  } = props

  const offerPageDispatch = useOfferPageDispatch()
  const guardBookingFlow = useMultiRoomBookingGuard()
  const invalidRoomSelection = !rooms || isRoomsInvalid(rooms)
  const viewOptionsLabel = useMemo(() => {
    if (!checkIn) return 'Check availability'
    return 'Select room'
  }, [checkIn])

  const onViewOptionsClick = useCallback(async(e: React.MouseEvent<HTMLButtonElement>) => {
    if (guardBookingFlow()) return
    onClick?.(e)

    // TEMP: Remove this logic when "006RF00000IRdvlYAD" becomes unavailable
    const skipRoomSelection = packageOptions.length ? packageOptions[0].package.offerId === '006RF00000IRdvlYAD' : false

    if (invalidRoomSelection || !durationSelected) {
      e.preventDefault()
      offerPageDispatch({
        type: OfferPageActions.checkAvailability,
        filter: 'capacity',
        room: skipRoomSelection ? undefined : roomType,
        flights: !!isFlightSelected,
      })
      return
    }

    if (!checkIn) {
      e.preventDefault()
      offerPageDispatch({
        type: OfferPageActions.checkAvailability,
        filter: 'checkin',
        room: skipRoomSelection ? undefined : roomType,
        flights: !!isFlightSelected,
      })
      return
    }

    // Reset bedbank room id for Single Property pages
    offerPageDispatch({ type: OfferPageActions.setBedbankRoomId, bedbankRoomId: undefined })
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'packageSelection' })
  }, [guardBookingFlow, onClick, invalidRoomSelection, durationSelected, checkIn, offerPageDispatch, roomType, isFlightSelected, packageOptions])

  return (
    <TextButton {...rest} kind="primary" onClick={onViewOptionsClick}>
      {viewOptionsLabel}
    </TextButton>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  rooms: state.rooms,
  checkIn: state.checkInDate,
  packageOptions: getDurationPackageOptions(state, appState),
  isFlightSelected: state.flights,
  activeFilter: state.activeFilter,
  durationSelected: state.durationSelected,
}), AccommodationMobileViewOptionsButton)
